import { supabase } from '../lib/supabase';

export const accountService = {
  /**
   * Get all accounts (tenants/organizations)
   * @returns {Promise<Array>} Array of account objects
   */
  async getAll() {
    try {
      const { data, error } = await supabase
        ?.from('tenants')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(account => ({
        id: account?.id,
        company: account?.name,
        industry: account?.industry_id,
        revenue: account?.annual_revenue || '$0',
        contactCount: 0,
        dealValue: '$0',
        lastActivity: account?.updated_at,
        owner: account?.created_by,
        region: account?.region || 'N/A'
      })) || [];
    } catch (error) {
      console.error('Error fetching accounts:', error);
      throw error;
    }
  },

  /**
   * Create new account
   * @param {Object} accountData - Account data to create
   * @returns {Promise<Object>} Created account object
   */
  async create(accountData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('tenants')
        ?.insert({
          name: accountData?.company,
          subdomain: accountData?.company?.toLowerCase()?.replace(/[^a-z0-9]/g, '-'),
          industry_id: accountData?.industry,
          subscription_tier: 'free',
          subscription_status: 'active',
          is_active: true
        })
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        company: data?.name,
        industry: data?.industry_id,
        revenue: '$0',
        contactCount: 0,
        dealValue: '$0',
        lastActivity: data?.created_at,
        owner: data?.created_by,
        region: 'N/A'
      };
    } catch (error) {
      console.error('Error creating account:', error);
      throw error;
    }
  },

  /**
   * Update existing account
   * @param {string} accountId - Account ID to update
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated account object
   */
  async update(accountId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('tenants')
        ?.update({
          name: updates?.company,
          industry_id: updates?.industry,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', accountId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        company: data?.name,
        industry: data?.industry_id,
        revenue: '$0',
        lastActivity: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating account:', error);
      throw error;
    }
  },

  /**
   * Delete account
   * @param {string} accountId - Account ID to delete
   * @returns {Promise<void>}
   */
  async delete(accountId) {
    try {
      const { error } = await supabase
        ?.from('tenants')
        ?.delete()
        ?.eq('id', accountId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  }
};